import { useState, useEffect, useRef, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Alert,
  AlertTitle,
  Box,
  Paper,
  Divider,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material'
import {
  StructuredQuestion,
  QuestionFormaterResponse,
} from '../interfaces/questions'
import QuestionCard from './QuestionCard'
import DraggableQuestionCard from './DraggableQuestionCard'
import InsertQuestionDialog from './InsertQuestionDialog'
import DeleteQuestionDialog from './DeleteQuestionDialog'
import { computeSubNumber, applyNumberRename, renumberSequentially, shiftSubNumbers, getNumberingChanges, NumberingChange } from '../utils/questionUtils'
import { HelpOutline, OpenInNew } from '@mui/icons-material'
import Link from '@mui/material/Link'
import Chip from '@mui/material/Chip'
import AddIcon from '@mui/icons-material/Add'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import CheckIcon from '@mui/icons-material/Check'
import {
  findQuestionInRawText,
  getHighlightedSegments,
} from '../utils/questionHighlight'

interface AIFormatReviewProps {
  open: boolean
  rawText: string
  formatResult: QuestionFormaterResponse | null
  onClose: () => void
  onRetry: () => void
  onAccept: (editedQuestions: StructuredQuestion[]) => void
  onQuestionUpdate?: (index: number, updated: StructuredQuestion) => void
  hideRawText?: boolean
  sourceName?: string
}

export default function AIFormatReview({
  open,
  rawText,
  formatResult,
  onClose,
  onRetry,
  onAccept,
  onQuestionUpdate,
  hideRawText = false,
  sourceName,
}: AIFormatReviewProps) {
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<
    number | null
  >(null)
  const [reorderMode, setReorderMode] = useState(false)
  const [questions, setQuestions] = useState<StructuredQuestion[]>(
    formatResult?.questions || [],
  )
  const [pendingInsert, setPendingInsert] = useState<{
    atIndex: number
    position: 'above' | 'below'
  } | null>(null)
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null,
  )
  const [numberingChanges, setNumberingChanges] = useState<NumberingChange[]>([])
  const editStartNumberRef = useRef<string>('')
  const [duplicateError, setDuplicateError] = useState('')
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<
    number | null
  >(null)
  const rawTextContainerRef = useRef<HTMLDivElement>(null)
  const highlightRef = useRef<HTMLSpanElement>(null)

  // Determine which question to highlight (editing takes precedence over selected)
  const activeQuestionIndex = editingQuestionIndex ?? selectedQuestionIndex

  // Calculate the highlight range for the active question
  const highlightRange = useMemo(() => {
    if (activeQuestionIndex === null || !rawText) return null
    const question = questions[activeQuestionIndex]
    if (!question) return null
    return findQuestionInRawText(question.question, rawText)
  }, [activeQuestionIndex, questions, rawText])

  // Get highlighted segments for rendering
  const highlightedSegments = useMemo(() => {
    return getHighlightedSegments(rawText, highlightRange)
  }, [rawText, highlightRange])

  // Scroll to highlighted text when it changes
  useEffect(() => {
    if (highlightRef.current && rawTextContainerRef.current) {
      const container = rawTextContainerRef.current
      const highlight = highlightRef.current

      // Calculate position relative to container
      const containerRect = container.getBoundingClientRect()
      const highlightRect = highlight.getBoundingClientRect()

      // Check if highlight is outside visible area
      const isAboveViewport = highlightRect.top < containerRect.top
      const isBelowViewport = highlightRect.bottom > containerRect.bottom

      if (isAboveViewport || isBelowViewport) {
        highlight.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }
  }, [highlightRange])

  // update when formatResult changes
  useEffect(() => {
    if (formatResult?.questions) {
      setQuestions(formatResult.questions)
    }
  }, [formatResult])

  const handleQuestionUpdate = (index: number, updated: StructuredQuestion) => {
    const newQuestions = [...questions]
    newQuestions[index] = { ...updated, index }
    setQuestions(newQuestions)
    if (onQuestionUpdate) {
      onQuestionUpdate(index, updated)
    }
  }

  const handleDeleteConfirm = (remapTo: string | null) => {
    if (pendingDeleteIndex === null) return
    const index = pendingDeleteIndex
    const deletedNumber = questions[index].question_number

    let updated = [...questions]
    if (remapTo) {
      const mapping = new Map([[deletedNumber, remapTo]])
      updated = applyNumberRename(updated, mapping)
    }

    const filtered = updated
      .filter((_, i) => i !== index)
      .map((q, i) => ({ ...q, index: i }))

    const deletedNumbers = remapTo ? new Set<string>() : new Set([deletedNumber])
    const finalQuestions = renumberSequentially(filtered, deletedNumbers).map(
      (q, i) => ({ ...q, index: i }),
    )
    setQuestions(finalQuestions)
    setPendingDeleteIndex(null)
    if (editingQuestionIndex === index) {
      setEditingQuestionIndex(null)
    }
  }

  const handleUpdateQuestionNumber = (index: number, newNumber: string) => {
    if (!newNumber.trim()) return
    const trimmed = newNumber.trim()
    const isDuplicate = questions.some(
      (q, i) => i !== index && q.question_number === trimmed,
    )
    if (isDuplicate) {
      setDuplicateError(`Question number "${trimmed}" already exists`)
      return
    }
    setDuplicateError('')
    const oldNumber = questions[index].question_number
    const updated = questions.map((q, i) =>
      i === index ? { ...q, question_number: trimmed } : q,
    )
    const mapping = new Map([[oldNumber, trimmed]])
    const result = applyNumberRename(updated, mapping)
    setQuestions(result)
    const changes = getNumberingChanges(result)
    if (changes.length > 0) setNumberingChanges(changes)
  }

  const handleAddNewQuestion = () => {
    const newQuestion: StructuredQuestion = {
      index: questions.length,
      question_number: '',
      question_type: 'text',
      question: '',
      attachment_required: false,
      required: true,
    }
    const newQuestions = [...questions, newQuestion]
    const renumbered = renumberSequentially(newQuestions).map((q, i) => ({
      ...q,
      index: i,
    }))
    setQuestions(renumbered)
    setEditingQuestionIndex(renumbered.length - 1)
  }

  const handleInsertConfirm = (choice: 'sub' | 'renumber') => {
    if (!pendingInsert) return
    const { atIndex, position } = pendingInsert
    const insertIndex = position === 'above' ? atIndex : atIndex + 1

    let questionNumber: string
    let sourceQuestions = questions

    if (choice === 'sub') {
      const clickedQuestion = questions[atIndex]
      const clickedSuffix =
        clickedQuestion.question_number.match(/[a-z]$/i)?.[0] ?? ''
      const isInsertAboveSub = position === 'above' && !!clickedSuffix

      if (isInsertAboveSub) {
        const base = clickedQuestion.question_number.replace(/[a-z]$/i, '')
        sourceQuestions = shiftSubNumbers(questions, base, clickedSuffix)
        questionNumber = clickedQuestion.question_number
      } else {
        const referenceIndex = position === 'above' ? atIndex - 1 : atIndex
        const referenceQuestion = questions[referenceIndex]
        questionNumber =
          computeSubNumber(referenceQuestion.question_number, questions) ??
          String(insertIndex + 1)
      }
    } else {
      questionNumber = '' // renumberSequentially will assign the correct integer
    }

    const newQuestion: StructuredQuestion = {
      index: insertIndex,
      question_number: questionNumber,
      question_type: 'text',
      question: '',
      attachment_required: false,
      required: true,
    }

    const spliced = [...sourceQuestions]
    spliced.splice(insertIndex, 0, newQuestion)

    let finalQuestions: StructuredQuestion[]
    if (choice === 'renumber') {
      finalQuestions = renumberSequentially(spliced).map((q, i) => ({
        ...q,
        index: i,
      }))
    } else {
      finalQuestions = spliced.map((q, i) => ({ ...q, index: i }))
    }

    setQuestions(finalQuestions)
    setEditingQuestionIndex(insertIndex)
    setPendingInsert(null)
  }

  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return

    const newQuestions = [...questions]
    const [movedQuestion] = newQuestions.splice(fromIndex, 1)
    newQuestions.splice(toIndex, 0, movedQuestion)

    const reindexed = renumberSequentially(newQuestions).map((q, i) => ({
      ...q,
      index: i,
    }))
    setQuestions(reindexed)
  }

  if (!formatResult) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {hideRawText ? 'Imported Questions Review' : 'Auto Format Question Review'}{' '}
        - {formatResult.questions.length} Questions Parsed
      </DialogTitle>
      <DialogContent>
        {sourceName && (
          <Chip
            label={
              sourceName.startsWith('http') ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Source:
                  <Link
                    href={sourceName}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {sourceName.length > 60 ? sourceName.slice(0, 60) + '...' : sourceName}
                    <OpenInNew sx={{ fontSize: 14 }} />
                  </Link>
                </Box>
              ) : (
                `Source: ${sourceName}`
              )
            }
            variant="outlined"
            sx={{ mt: 2, mb: 1, maxWidth: '100%' }}
          />
        )}
        <Accordion sx={{ mt: 2, mb: 4 }}>
          <AccordionSummary expandIcon={<HelpOutline />}>
            {' '}
            View Formatter Details{' '}
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {formatResult.parsing_notes}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Confidence: {formatResult.confidence}
            </Typography>
          </AccordionDetails>
        </Accordion>

        {formatResult.warnings && formatResult.warnings.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Issues Found — Please Review Before Accepting</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {formatResult.warnings.map((item, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{item}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {formatResult.ambiguities && formatResult.ambiguities.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Areas to Verify</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {formatResult.ambiguities.map((item, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{item}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Grid container spacing={3}>
          {!hideRawText && (
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Raw Questions
              </Typography>
              <Paper
                ref={rawTextContainerRef}
                sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  maxHeight: '600px',
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                >
                  {highlightedSegments.map((segment, idx) =>
                    segment.isHighlighted ? (
                      <span
                        key={idx}
                        ref={highlightRef}
                        style={{
                          backgroundColor: '#fff3cd',
                          borderRadius: '2px',
                          padding: '1px 0',
                          boxShadow: '0 0 0 2px #ffc107',
                        }}
                      >
                        {segment.text}
                      </span>
                    ) : (
                      <span key={idx}>{segment.text}</span>
                    ),
                  )}
                </Typography>
              </Paper>
            </Grid>
          )}
          <Grid item xs={12} md={hideRawText ? 8 : 6} sx={hideRawText ? { mx: 'auto' } : undefined}>
            <Box sx={hideRawText ? { maxWidth: 860, mx: 'auto' } : undefined}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">
                Structured Questions ({questions.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={reorderMode ? <CheckIcon /> : <SwapVertIcon />}
                  variant={reorderMode ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    setReorderMode(!reorderMode)
                    setEditingQuestionIndex(null)
                  }}
                >
                  {reorderMode ? 'Done' : 'Reorder'}
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={handleAddNewQuestion}
                  disabled={reorderMode}
                >
                  Add
                </Button>
              </Box>
            </Box>
            {duplicateError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDuplicateError('')}>
                {duplicateError}
              </Alert>
            )}
            <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
              {reorderMode ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Drag and drop questions to reorder them.
                  </Alert>
                  {questions.map((q, index) => (
                    <DraggableQuestionCard
                      key={`review-question-${index}`}
                      question={q}
                      index={index}
                      onReorder={handleReorder}
                      onUpdateQuestionNumber={(num) =>
                        handleUpdateQuestionNumber(index, num)
                      }
                      onInsertAbove={() =>
                        setPendingInsert({ atIndex: index, position: 'above' })
                      }
                      onInsertBelow={() =>
                        setPendingInsert({ atIndex: index, position: 'below' })
                      }
                    />
                  ))}
                </>
              ) : (
                questions.map((q, index) => (
                  <Box
                    key={`review-question-${index}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedQuestionIndex(
                        selectedQuestionIndex === index ? null : index,
                      )
                    }}
                    sx={{
                      borderLeft:
                        activeQuestionIndex === index
                          ? '3px solid #ffc107'
                          : '3px solid transparent',
                      backgroundColor:
                        activeQuestionIndex === index
                          ? 'rgba(255, 193, 7, 0.1)'
                          : 'transparent',
                      pl: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <QuestionCard
                      question={q}
                      allQuestions={questions}
                      onEdit={() => {
                        if (editingQuestionIndex === index) {
                          const currentNumber = questions[index].question_number
                          const isDuplicate = questions.some(
                            (q, i) =>
                              i !== index &&
                              q.question_number === currentNumber,
                          )
                          if (isDuplicate) {
                            setDuplicateError(
                              `Question number "${currentNumber}" already exists`,
                            )
                            return
                          }
                          setDuplicateError('')
                          setEditingQuestionIndex(null)
                          if (currentNumber !== editStartNumberRef.current) {
                            const changes = getNumberingChanges(questions)
                            if (changes.length > 0) setNumberingChanges(changes)
                          }
                        } else {
                          editStartNumberRef.current =
                            questions[index].question_number
                          setEditingQuestionIndex(index)
                        }
                      }}
                      onUpdate={(updated) =>
                        handleQuestionUpdate(index, updated)
                      }
                      onDelete={() => setPendingDeleteIndex(index)}
                      onInsertAbove={() =>
                        setPendingInsert({ atIndex: index, position: 'above' })
                      }
                      onInsertBelow={() =>
                        setPendingInsert({ atIndex: index, position: 'below' })
                      }
                      isEditing={editingQuestionIndex === index}
                    />
                  </Box>
                ))
              )}
            </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onRetry} variant="outlined">
            Retry Formatting
          </Button>
          <Button
            onClick={() => onAccept(questions)}
            variant="contained"
            color="primary"
          >
            Accept & Back to Edit
          </Button>
        </Box>
      </DialogActions>
      <InsertQuestionDialog
        open={pendingInsert !== null}
        atIndex={pendingInsert?.atIndex ?? 0}
        position={pendingInsert?.position ?? 'below'}
        allQuestions={questions}
        onConfirm={handleInsertConfirm}
        onClose={() => setPendingInsert(null)}
      />
      <DeleteQuestionDialog
        open={pendingDeleteIndex !== null}
        question={
          pendingDeleteIndex !== null
            ? questions[pendingDeleteIndex]
            : null
        }
        allQuestions={questions}
        onConfirm={handleDeleteConfirm}
        onClose={() => setPendingDeleteIndex(null)}
      />
      <Dialog
        open={numberingChanges.length > 0}
        onClose={() => setNumberingChanges([])}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Renumber questions?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following questions would be renumbered:
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {numberingChanges.map((change) => (
              <Box key={change.from} sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  <strong>Q{change.from}</strong> →{' '}
                  <strong>Q{change.to}</strong>
                  {change.questionText
                    ? ` — ${change.questionText.slice(0, 60)}${change.questionText.length > 60 ? '...' : ''}`
                    : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNumberingChanges([])}>Keep as is</Button>
          <Button
            variant="contained"
            onClick={() => {
              const renumbered = renumberSequentially(questions).map(
                (q, i) => ({ ...q, index: i }),
              )
              setQuestions(renumbered)
              setNumberingChanges([])
            }}
          >
            Renumber
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
